import { useState, type FormEvent } from "react";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { Route as RootRoute } from "./__root";
import { loginSchema } from "../schemas/login";
import { apiCall, ApiError, RateLimitedError } from "../lib/apiClient";
import { hydrateAuth, useAuthStore, type AuthUser } from "../lib/authStore";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Input } from "@/components/base/input/input";
import { Button } from "@/components/base/buttons/button";
import { UntitledLogo } from "@/components/foundations/logo/untitledui-logo";

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/login",
  validateSearch: (s: Record<string, unknown>) => ({
    next: typeof s.next === "string" ? s.next : undefined,
  }),
  component: LoginComponent,
});

interface LoginResponse {
  user_id: string;
  role: "globalAdmin" | "tenantAdmin" | "subAdmin" | "endUser";
  tenant_id: string | null;
  requires_consent_re_prompt?: boolean;
}

const setAuthenticatedFromLogin = (data: LoginResponse, email: string) => {
  const user: AuthUser = {
    userId: data.user_id,
    role: data.role,
    tenantId: data.tenant_id,
    email,
    requiresConsentRePrompt: data.requires_consent_re_prompt ?? false,
  };
  useAuthStore.getState().setAuthenticated(user);
};

function LoginComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const loginMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      apiCall<LoginResponse>("/api/public/auth/login", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: async (data, variables) => {
      setServerError(null);
      setAuthenticatedFromLogin(data, variables.email);
      await hydrateAuth().catch(() => {});
      if (data.requires_consent_re_prompt) {
        navigate({ to: "/consent/re-prompt" });
        return;
      }
      if (search.next) {
        try {
          const decoded = decodeURIComponent(search.next);
          const url = new URL(decoded, window.location.origin);
          if (url.origin === window.location.origin) {
            window.location.assign(url.toString());
            return;
          }
        } catch {
          // Fall through.
        }
      }
      if (data.role === "globalAdmin") {
        navigate({ to: "/admin/dashboard" });
      } else {
        navigate({ to: "/tenant/dashboard" });
      }
    },
    onError: (err: unknown) => {
      if (err instanceof RateLimitedError) {
        setServerError(`Příliš mnoho pokusů. Zkuste to znovu za ${err.retryAfterSeconds} s.`);
        return;
      }
      if (err instanceof ApiError) {
        if (err.status === 423 || err.errorCode === "ACCOUNT_LOCKED") {
          setServerError("Účet je dočasně uzamčen po opakovaných neúspěšných pokusech.");
          return;
        }
        if (err.status === 401) {
          setServerError("Neplatné přihlašovací údaje.");
          return;
        }
        setServerError(err.message || "Přihlášení se nezdařilo.");
        return;
      }
      setServerError("Neznámá chyba sítě.");
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      const fe: { email?: string; password?: string } = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        // První issue per pole (zod vrací pro prázdný string i min i email format).
        if ((key === "email" || key === "password") && !fe[key]) fe[key] = issue.message;
      }
      setFieldErrors(fe);
      return;
    }
    setFieldErrors({});
    setServerError(null);
    loginMutation.mutate(parsed.data);
  };

  const authState = useAuthStore((s) => s.auth);

  return (
    <section className="min-h-screen bg-primary px-4 py-12 sm:bg-secondary md:px-8 md:pt-24">
      <div className="flex w-full flex-col gap-6 bg-primary sm:mx-auto sm:max-w-110 sm:rounded-2xl sm:px-10 sm:py-8 sm:shadow-sm">
        <div className="flex flex-col items-center gap-5 text-center">
          <UntitledLogo className="text-2xl" />
          {authState.state === "authenticated" ? (
            <div className="flex flex-col gap-2">
              <h1 className="text-xl font-semibold text-primary md:text-display-xs">Přihlášený</h1>
              <p className="text-md text-tertiary">Aktivní session existuje. Přesměrování…</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2 md:gap-3">
              <h1 className="text-xl font-semibold text-primary md:text-display-xs">Přihlášení do Axigon</h1>
              <p className="text-md text-tertiary">Vítejte zpět. Zadejte své přihlašovací údaje.</p>
            </div>
          )}
        </div>

        {authState.state === "authenticated" ? (
          <Button size="lg" href="/">
            Pokračovat
          </Button>
        ) : (
          <>
            {serverError && (
              <InlineAlert variant="error" title="Chyba přihlášení">
                {serverError}
              </InlineAlert>
            )}

            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6">
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-1.5">
                  <Input
                    type="email"
                    label="E-mail"
                    placeholder="vas@email.cz"
                    size="lg"
                    autoComplete="email"
                    value={email}
                    onChange={setEmail}
                    isInvalid={Boolean(fieldErrors.email)}
                  />
                  {fieldErrors.email && (
                    <p className="text-sm text-error-primary">{fieldErrors.email}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Input
                    type="password"
                    label="Heslo"
                    placeholder="••••••••"
                    size="lg"
                    autoComplete="current-password"
                    value={password}
                    onChange={setPassword}
                    isInvalid={Boolean(fieldErrors.password)}
                  />
                  {fieldErrors.password && (
                    <p className="text-sm text-error-primary">{fieldErrors.password}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <Button color="link-color" size="md" href="/forgot-password" className="ml-auto">
                  Zapomněli jste heslo?
                </Button>
              </div>

              <Button type="submit" size="lg" isLoading={loginMutation.isPending}>
                Přihlásit se
              </Button>
            </form>
          </>
        )}

        <p className="text-center text-xs text-tertiary">
          axigon · refakturace tankování per Středisko
        </p>
      </div>
    </section>
  );
}
